/**
 * Wallstraight loader flowfield (Parts 3–4).
 * Noise library: simplex-noise MIT (c) Jonas Wagner, bundled from unpkg 4.0.1.
 */
(function (global) {
  'use strict';
/*
 * A fast javascript implementation of simplex noise by Jonas Wagner

Based on a speed-improved simplex noise algorithm for 2D, 3D and 4D in Java.
Which is based on example code by Stefan Gustavson (stegu@itn.liu.se).
With Optimisations by Peter Eastman (peastman@drizzle.stanford.edu).
Better rank ordering method by Stefan Gustavson in 2012.

 Copyright (c) 2022 Jonas Wagner

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:

 The above copyright notice and this permission notice shall be included in all
 copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 SOFTWARE.
 */
// these #__PURE__ comments help uglifyjs with dead code removal
// 
const F2 = /*#__PURE__*/ 0.5 * (Math.sqrt(3.0) - 1.0);
const G2 = /*#__PURE__*/ (3.0 - Math.sqrt(3.0)) / 6.0;
const F3 = 1.0 / 3.0;
const G3 = 1.0 / 6.0;
const F4 = /*#__PURE__*/ (Math.sqrt(5.0) - 1.0) / 4.0;
const G4 = /*#__PURE__*/ (5.0 - Math.sqrt(5.0)) / 20.0;
// I'm really not sure why this | 0 (basically a coercion to int)
// is making this faster but I get ~5 million ops/sec more on the
// benchmarks across the board or a ~10% speedup.
const fastFloor = (x) => Math.floor(x) | 0;
const grad2 = /*#__PURE__*/ new Float64Array([1, 1,
    -1, 1,
    1, -1,
    -1, -1,
    1, 0,
    -1, 0,
    1, 0,
    -1, 0,
    0, 1,
    0, -1,
    0, 1,
    0, -1]);
// double seems to be faster than single or int's
// probably because most operations are in double precision
const grad3 = /*#__PURE__*/ new Float64Array([1, 1, 0,
    -1, 1, 0,
    1, -1, 0,
    -1, -1, 0,
    1, 0, 1,
    -1, 0, 1,
    1, 0, -1,
    -1, 0, -1,
    0, 1, 1,
    0, -1, 1,
    0, 1, -1,
    0, -1, -1]);
// double is a bit quicker here as well
const grad4 = /*#__PURE__*/ new Float64Array([0, 1, 1, 1, 0, 1, 1, -1, 0, 1, -1, 1, 0, 1, -1, -1,
    0, -1, 1, 1, 0, -1, 1, -1, 0, -1, -1, 1, 0, -1, -1, -1,
    1, 0, 1, 1, 1, 0, 1, -1, 1, 0, -1, 1, 1, 0, -1, -1,
    -1, 0, 1, 1, -1, 0, 1, -1, -1, 0, -1, 1, -1, 0, -1, -1,
    1, 1, 0, 1, 1, 1, 0, -1, 1, -1, 0, 1, 1, -1, 0, -1,
    -1, 1, 0, 1, -1, 1, 0, -1, -1, -1, 0, 1, -1, -1, 0, -1,
    1, 1, 1, 0, 1, 1, -1, 0, 1, -1, 1, 0, 1, -1, -1, 0,
    -1, 1, 1, 0, -1, 1, -1, 0, -1, -1, 1, 0, -1, -1, -1, 0]);
/**
 * Creates a 2D noise function
 * @param random the random function that will be used to build the permutation table
 * @returns {NoiseFunction2D}
 */
function createNoise2D(random = Math.random) {
    const perm = buildPermutationTable(random);
    // precalculating this yields a little ~3% performance improvement.
    const permGrad2x = new Float64Array(perm).map(v => grad2[(v % 12) * 2]);
    const permGrad2y = new Float64Array(perm).map(v => grad2[(v % 12) * 2 + 1]);
    return function noise2D(x, y) {
        // if(!isFinite(x) || !isFinite(y)) return 0;
        let n0 = 0; // Noise contributions from the three corners
        let n1 = 0;
        let n2 = 0;
        // Skew the input space to determine which simplex cell we're in
        const s = (x + y) * F2; // Hairy factor for 2D
        const i = fastFloor(x + s);
        const j = fastFloor(y + s);
        const t = (i + j) * G2;
        const X0 = i - t; // Unskew the cell origin back to (x,y) space
        const Y0 = j - t;
        const x0 = x - X0; // The x,y distances from the cell origin
        const y0 = y - Y0;
        // For the 2D case, the simplex shape is an equilateral triangle.
        // Determine which simplex we are in.
        let i1, j1; // Offsets for second (middle) corner of simplex in (i,j) coords
        if (x0 > y0) {
            i1 = 1;
            j1 = 0;
        } // lower triangle, XY order: (0,0)->(1,0)->(1,1)
        else {
            i1 = 0;
            j1 = 1;
        } // upper triangle, YX order: (0,0)->(0,1)->(1,1)
        // A step of (1,0) in (i,j) means a step of (1-c,-c) in (x,y), and
        // a step of (0,1) in (i,j) means a step of (-c,1-c) in (x,y), where
        // c = (3-sqrt(3))/6
        const x1 = x0 - i1 + G2; // Offsets for middle corner in (x,y) unskewed coords
        const y1 = y0 - j1 + G2;
        const x2 = x0 - 1.0 + 2.0 * G2; // Offsets for last corner in (x,y) unskewed coords
        const y2 = y0 - 1.0 + 2.0 * G2;
        // Work out the hashed gradient indices of the three simplex corners
        const ii = i & 255;
        const jj = j & 255;
        // Calculate the contribution from the three corners
        let t0 = 0.5 - x0 * x0 - y0 * y0;
        if (t0 >= 0) {
            const gi0 = ii + perm[jj];
            const g0x = permGrad2x[gi0];
            const g0y = permGrad2y[gi0];
            t0 *= t0;
            // n0 = t0 * t0 * (grad2[gi0] * x0 + grad2[gi0 + 1] * y0); // (x,y) of grad3 used for 2D gradient
            n0 = t0 * t0 * (g0x * x0 + g0y * y0);
        }
        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 >= 0) {
            const gi1 = ii + i1 + perm[jj + j1];
            const g1x = permGrad2x[gi1];
            const g1y = permGrad2y[gi1];
            t1 *= t1;
            // n1 = t1 * t1 * (grad2[gi1] * x1 + grad2[gi1 + 1] * y1);
            n1 = t1 * t1 * (g1x * x1 + g1y * y1);
        }
        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 >= 0) {
            const gi2 = ii + 1 + perm[jj + 1];
            const g2x = permGrad2x[gi2];
            const g2y = permGrad2y[gi2];
            t2 *= t2;
            // n2 = t2 * t2 * (grad2[gi2] * x2 + grad2[gi2 + 1] * y2);
            n2 = t2 * t2 * (g2x * x2 + g2y * y2);
        }
        // Add contributions from each corner to get the final noise value.
        // The result is scaled to return values in the interval [-1,1].
        return 70.0 * (n0 + n1 + n2);
    };
}
/**
 * Creates a 3D noise function
 * @param random the random function that will be used to build the permutation table
 * @returns {NoiseFunction3D}
 */
function createNoise3D(random = Math.random) {
    const perm = buildPermutationTable(random);
    // precalculating these seems to yield a speedup of over 15%
    const permGrad3x = new Float64Array(perm).map(v => grad3[(v % 12) * 3]);
    const permGrad3y = new Float64Array(perm).map(v => grad3[(v % 12) * 3 + 1]);
    const permGrad3z = new Float64Array(perm).map(v => grad3[(v % 12) * 3 + 2]);
    return function noise3D(x, y, z) {
        let n0, n1, n2, n3; // Noise contributions from the four corners
        // Skew the input space to determine which simplex cell we're in
        const s = (x + y + z) * F3; // Very nice and simple skew factor for 3D
        const i = fastFloor(x + s);
        const j = fastFloor(y + s);
        const k = fastFloor(z + s);
        const t = (i + j + k) * G3;
        const X0 = i - t; // Unskew the cell origin back to (x,y,z) space
        const Y0 = j - t;
        const Z0 = k - t;
        const x0 = x - X0; // The x,y,z distances from the cell origin
        const y0 = y - Y0;
        const z0 = z - Z0;
        // For the 3D case, the simplex shape is a slightly irregular tetrahedron.
        // Determine which simplex we are in.
        let i1, j1, k1; // Offsets for second corner of simplex in (i,j,k) coords
        let i2, j2, k2; // Offsets for third corner of simplex in (i,j,k) coords
        if (x0 >= y0) {
            if (y0 >= z0) {
                i1 = 1;
                j1 = 0;
                k1 = 0;
                i2 = 1;
                j2 = 1;
                k2 = 0;
            } // X Y Z order
            else if (x0 >= z0) {
                i1 = 1;
                j1 = 0;
                k1 = 0;
                i2 = 1;
                j2 = 0;
                k2 = 1;
            } // X Z Y order
            else {
                i1 = 0;
                j1 = 0;
                k1 = 1;
                i2 = 1;
                j2 = 0;
                k2 = 1;
            } // Z X Y order
        }
        else { // x0<y0
            if (y0 < z0) {
                i1 = 0;
                j1 = 0;
                k1 = 1;
                i2 = 0;
                j2 = 1;
                k2 = 1;
            } // Z Y X order
            else if (x0 < z0) {
                i1 = 0;
                j1 = 1;
                k1 = 0;
                i2 = 0;
                j2 = 1;
                k2 = 1;
            } // Y Z X order
            else {
                i1 = 0;
                j1 = 1;
                k1 = 0;
                i2 = 1;
                j2 = 1;
                k2 = 0;
            } // Y X Z order
        }
        // A step of (1,0,0) in (i,j,k) means a step of (1-c,-c,-c) in (x,y,z),
        // a step of (0,1,0) in (i,j,k) means a step of (-c,1-c,-c) in (x,y,z), and
        // a step of (0,0,1) in (i,j,k) means a step of (-c,-c,1-c) in (x,y,z), where
        // c = 1/6.
        const x1 = x0 - i1 + G3; // Offsets for second corner in (x,y,z) coords
        const y1 = y0 - j1 + G3;
        const z1 = z0 - k1 + G3;
        const x2 = x0 - i2 + 2.0 * G3; // Offsets for third corner in (x,y,z) coords
        const y2 = y0 - j2 + 2.0 * G3;
        const z2 = z0 - k2 + 2.0 * G3;
        const x3 = x0 - 1.0 + 3.0 * G3; // Offsets for last corner in (x,y,z) coords
        const y3 = y0 - 1.0 + 3.0 * G3;
        const z3 = z0 - 1.0 + 3.0 * G3;
        // Work out the hashed gradient indices of the four simplex corners
        const ii = i & 255;
        const jj = j & 255;
        const kk = k & 255;
        // Calculate the contribution from the four corners
        let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
        if (t0 < 0)
            n0 = 0.0;
        else {
            const gi0 = ii + perm[jj + perm[kk]];
            t0 *= t0;
            n0 = t0 * t0 * (permGrad3x[gi0] * x0 + permGrad3y[gi0] * y0 + permGrad3z[gi0] * z0);
        }
        let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
        if (t1 < 0)
            n1 = 0.0;
        else {
            const gi1 = ii + i1 + perm[jj + j1 + perm[kk + k1]];
            t1 *= t1;
            n1 = t1 * t1 * (permGrad3x[gi1] * x1 + permGrad3y[gi1] * y1 + permGrad3z[gi1] * z1);
        }
        let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
        if (t2 < 0)
            n2 = 0.0;
        else {
            const gi2 = ii + i2 + perm[jj + j2 + perm[kk + k2]];
            t2 *= t2;
            n2 = t2 * t2 * (permGrad3x[gi2] * x2 + permGrad3y[gi2] * y2 + permGrad3z[gi2] * z2);
        }
        let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
        if (t3 < 0)
            n3 = 0.0;
        else {
            const gi3 = ii + 1 + perm[jj + 1 + perm[kk + 1]];
            t3 *= t3;
            n3 = t3 * t3 * (permGrad3x[gi3] * x3 + permGrad3y[gi3] * y3 + permGrad3z[gi3] * z3);
        }
        // Add contributions from each corner to get the final noise value.
        // The result is scaled to stay just inside [-1,1]
        return 32.0 * (n0 + n1 + n2 + n3);
    };
}
/**
 * Creates a 4D noise function
 * @param random the random function that will be used to build the permutation table
 * @returns {NoiseFunction4D}
 */
function createNoise4D(random = Math.random) {
    const perm = buildPermutationTable(random);
    // precalculating these leads to a ~10% speedup
    const permGrad4x = new Float64Array(perm).map(v => grad4[(v % 32) * 4]);
    const permGrad4y = new Float64Array(perm).map(v => grad4[(v % 32) * 4 + 1]);
    const permGrad4z = new Float64Array(perm).map(v => grad4[(v % 32) * 4 + 2]);
    const permGrad4w = new Float64Array(perm).map(v => grad4[(v % 32) * 4 + 3]);
    return function noise4D(x, y, z, w) {
        let n0, n1, n2, n3, n4; // Noise contributions from the five corners
        // Skew the (x,y,z,w) space to determine which cell of 24 simplices we're in
        const s = (x + y + z + w) * F4; // Factor for 4D skewing
        const i = fastFloor(x + s);
        const j = fastFloor(y + s);
        const k = fastFloor(z + s);
        const l = fastFloor(w + s);
        const t = (i + j + k + l) * G4; // Factor for 4D unskewing
        const X0 = i - t; // Unskew the cell origin back to (x,y,z,w) space
        const Y0 = j - t;
        const Z0 = k - t;
        const W0 = l - t;
        const x0 = x - X0; // The x,y,z,w distances from the cell origin
        const y0 = y - Y0;
        const z0 = z - Z0;
        const w0 = w - W0;
        // For the 4D case, the simplex is a 4D shape I won't even try to describe.
        // To find out which of the 24 possible simplices we're in, we need to
        // determine the magnitude ordering of x0, y0, z0 and w0.
        // Six pair-wise comparisons are performed between each possible pair
        // of the four coordinates, and the results are used to rank the numbers.
        let rankx = 0;
        let ranky = 0;
        let rankz = 0;
        let rankw = 0;
        if (x0 > y0)
            rankx++;
        else
            ranky++;
        if (x0 > z0)
            rankx++;
        else
            rankz++;
        if (x0 > w0)
            rankx++;
        else
            rankw++;
        if (y0 > z0)
            ranky++;
        else
            rankz++;
        if (y0 > w0)
            ranky++;
        else
            rankw++;
        if (z0 > w0)
            rankz++;
        else
            rankw++;
        // simplex[c] is a 4-vector with the numbers 0, 1, 2 and 3 in some order.
        // Many values of c will never occur, since e.g. x>y>z>w makes x<z, y<w and x<w
        // impossible. Only the 24 indices which have non-zero entries make any sense.
        // We use a thresholding to set the coordinates in turn from the largest magnitude.
        // Rank 3 denotes the largest coordinate.
        // Rank 2 denotes the second largest coordinate.
        // Rank 1 denotes the second smallest coordinate.
        // The integer offsets for the second simplex corner
        const i1 = rankx >= 3 ? 1 : 0;
        const j1 = ranky >= 3 ? 1 : 0;
        const k1 = rankz >= 3 ? 1 : 0;
        const l1 = rankw >= 3 ? 1 : 0;
        // The integer offsets for the third simplex corner
        const i2 = rankx >= 2 ? 1 : 0;
        const j2 = ranky >= 2 ? 1 : 0;
        const k2 = rankz >= 2 ? 1 : 0;
        const l2 = rankw >= 2 ? 1 : 0;
        // The integer offsets for the fourth simplex corner
        const i3 = rankx >= 1 ? 1 : 0;
        const j3 = ranky >= 1 ? 1 : 0;
        const k3 = rankz >= 1 ? 1 : 0;
        const l3 = rankw >= 1 ? 1 : 0;
        // The fifth corner has all coordinate offsets = 1, so no need to compute that.
        const x1 = x0 - i1 + G4; // Offsets for second corner in (x,y,z,w) coords
        const y1 = y0 - j1 + G4;
        const z1 = z0 - k1 + G4;
        const w1 = w0 - l1 + G4;
        const x2 = x0 - i2 + 2.0 * G4; // Offsets for third corner in (x,y,z,w) coords
        const y2 = y0 - j2 + 2.0 * G4;
        const z2 = z0 - k2 + 2.0 * G4;
        const w2 = w0 - l2 + 2.0 * G4;
        const x3 = x0 - i3 + 3.0 * G4; // Offsets for fourth corner in (x,y,z,w) coords
        const y3 = y0 - j3 + 3.0 * G4;
        const z3 = z0 - k3 + 3.0 * G4;
        const w3 = w0 - l3 + 3.0 * G4;
        const x4 = x0 - 1.0 + 4.0 * G4; // Offsets for last corner in (x,y,z,w) coords
        const y4 = y0 - 1.0 + 4.0 * G4;
        const z4 = z0 - 1.0 + 4.0 * G4;
        const w4 = w0 - 1.0 + 4.0 * G4;
        // Work out the hashed gradient indices of the five simplex corners
        const ii = i & 255;
        const jj = j & 255;
        const kk = k & 255;
        const ll = l & 255;
        // Calculate the contribution from the five corners
        let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0 - w0 * w0;
        if (t0 < 0)
            n0 = 0.0;
        else {
            const gi0 = ii + perm[jj + perm[kk + perm[ll]]];
            t0 *= t0;
            n0 = t0 * t0 * (permGrad4x[gi0] * x0 + permGrad4y[gi0] * y0 + permGrad4z[gi0] * z0 + permGrad4w[gi0] * w0);
        }
        let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1 - w1 * w1;
        if (t1 < 0)
            n1 = 0.0;
        else {
            const gi1 = ii + i1 + perm[jj + j1 + perm[kk + k1 + perm[ll + l1]]];
            t1 *= t1;
            n1 = t1 * t1 * (permGrad4x[gi1] * x1 + permGrad4y[gi1] * y1 + permGrad4z[gi1] * z1 + permGrad4w[gi1] * w1);
        }
        let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2 - w2 * w2;
        if (t2 < 0)
            n2 = 0.0;
        else {
            const gi2 = ii + i2 + perm[jj + j2 + perm[kk + k2 + perm[ll + l2]]];
            t2 *= t2;
            n2 = t2 * t2 * (permGrad4x[gi2] * x2 + permGrad4y[gi2] * y2 + permGrad4z[gi2] * z2 + permGrad4w[gi2] * w2);
        }
        let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3 - w3 * w3;
        if (t3 < 0)
            n3 = 0.0;
        else {
            const gi3 = ii + i3 + perm[jj + j3 + perm[kk + k3 + perm[ll + l3]]];
            t3 *= t3;
            n3 = t3 * t3 * (permGrad4x[gi3] * x3 + permGrad4y[gi3] * y3 + permGrad4z[gi3] * z3 + permGrad4w[gi3] * w3);
        }
        let t4 = 0.6 - x4 * x4 - y4 * y4 - z4 * z4 - w4 * w4;
        if (t4 < 0)
            n4 = 0.0;
        else {
            const gi4 = ii + 1 + perm[jj + 1 + perm[kk + 1 + perm[ll + 1]]];
            t4 *= t4;
            n4 = t4 * t4 * (permGrad4x[gi4] * x4 + permGrad4y[gi4] * y4 + permGrad4z[gi4] * z4 + permGrad4w[gi4] * w4);
        }
        // Sum up and scale the result to cover the range [-1,1]
        return 27.0 * (n0 + n1 + n2 + n3 + n4);
    };
}
/**
 * Builds a random permutation table.
 * This is exported only for (internal) testing purposes.
 * Do not rely on this export.
 * @private
 */
function buildPermutationTable(random) {
    const tableSize = 512;
    const p = new Uint8Array(tableSize);
    for (let i = 0; i < tableSize / 2; i++) {
        p[i] = i;
    }
    for (let i = 0; i < tableSize / 2 - 1; i++) {
        const r = i + ~~(random() * (256 - i));
        const aux = p[i];
        p[i] = p[r];
        p[r] = aux;
    }
    for (let i = 256; i < tableSize; i++) {
        p[i] = p[i - 256];
    }
    return p;
}
//# sourceMappingURL=simplex-noise.js.map

  // --- Wallstraight loader flowfield (Tibixx / DonKarlssonSan concept; MIT simplex-noise above) ---

  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function Vector(x, y) {
    this.x = x;
    this.y = y;
  }
  Vector.prototype.addTo = function (v) {
    this.x += v.x;
    this.y += v.y;
    return this;
  };
  Vector.prototype.setLength = function (len) {
    const m = this.getLength();
    if (m) {
      const s = len / m;
      this.x *= s;
      this.y *= s;
    }
    return this;
  };
  Vector.prototype.getLength = function () {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  };
  Vector.prototype.setAngle = function (ang) {
    const l = this.getLength() || 1;
    this.x = Math.cos(ang) * l;
    this.y = Math.sin(ang) * l;
    return this;
  };
  Vector.prototype.div = function (s) {
    return new Vector(this.x / s, this.y / s);
  };

  let noise3D = null;
  const noise = {
    seed: function (seedVal) {
      const a =
        typeof seedVal === 'number' && Number.isFinite(seedVal)
          ? (seedVal >>> 0)
          : ((Math.random() * 0xffffffff) >>> 0);
      noise3D = createNoise3D(mulberry32(a));
    },
    simplex3: function (x, y, z) {
      return noise3D(x, y, z);
    },
    perlin3: function (x, y, z) {
      return noise3D(x + 19.17, y - 8.42, z * 1.001);
    },
  };

  let canvas = null;
  let ctx = null;
  let wrapEl = null;
  let ldElRef = null;
  let field = null;
  let w = 1;
  let h = 1;
  let fieldSize = 70;
  let fieldForce = 0.15;
  let noiseSpeed = 0.003;
  let noiseZ = 0;
  let particles = [];
  let particleCount = 2000;
  let particleSize = 0.9;
  let trailLength = 0.15;
  let hueBase = 10;
  let hueRange = 5;
  let maxSpeed = 2.5;
  let sORp = true;
  let columns = 1;
  let rows = 1;
  let rafId = null;
  let running = false;
  let resizeBound = false;

  function onFlowfieldResize() {
    if (!running || !ldElRef || !ldElRef.classList.contains('ld--flowfield-active')) return;
    if (!syncSize()) return;
    initParticles();
    initField();
  }

  function bindFlowfieldResize() {
    if (resizeBound) return;
    global.addEventListener('resize', onFlowfieldResize, { passive: true });
    try {
      global.addEventListener('orientationchange', onFlowfieldResize, { passive: true });
    } catch (e) {}
    resizeBound = true;
  }

  function unbindFlowfieldResize() {
    if (!resizeBound) return;
    global.removeEventListener('resize', onFlowfieldResize);
    try {
      global.removeEventListener('orientationchange', onFlowfieldResize);
    } catch (e) {}
    resizeBound = false;
  }

  function prefersReducedMotion() {
    try {
      return global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function particleTargetCount() {
    try {
      if (global.matchMedia && global.matchMedia('(max-width:600px)').matches) return 900;
    } catch (e) {}
    return 2000;
  }

  class Particle {
    constructor(x, y) {
      this.pos = new Vector(x, y);
      this.vel = new Vector(Math.random() - 0.5, Math.random() - 0.5);
      this.acc = new Vector(0, 0);
      this.hue = Math.random() * 30 - 15;
    }
    move(acc) {
      if (acc) this.acc.addTo(acc);
      this.vel.addTo(this.acc);
      this.pos.addTo(this.vel);
      if (this.vel.getLength() > maxSpeed) this.vel.setLength(maxSpeed);
      this.acc.setLength(0);
    }
    wrap() {
      if (this.pos.x > w) this.pos.x = 0;
      else if (this.pos.x < -this.fieldSize) this.pos.x = w - 1;
      if (this.pos.y > h) this.pos.y = 0;
      else if (this.pos.y < -this.fieldSize) this.pos.y = h - 1;
    }
  }

  function bindDom() {
    ldElRef = global.document && global.document.getElementById('ld');
    wrapEl = global.document && global.document.getElementById('ldFlowfield');
    canvas = global.document && global.document.getElementById('ldFlowfieldCanvas');
    ctx = canvas && canvas.getContext('2d', { alpha: false });
  }

  function initParticles() {
    particles = [];
    particleCount = particleTargetCount();
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle(Math.random() * w, Math.random() * h));
    }
  }

  function initField() {
    field = new Array(columns);
    for (let x = 0; x < columns; x++) {
      field[x] = new Array(rows);
      for (let y = 0; y < rows; y++) field[x][y] = new Vector(0, 0);
    }
  }

  function calcField() {
    if (sORp) {
      for (let x = 0; x < columns; x++) {
        for (let y = 0; y < rows; y++) {
          const angle = noise.simplex3(x / 20, y / 20, noiseZ) * Math.PI * 2;
          const len =
            noise.simplex3(x / 40 + 40000, y / 40 + 40000, noiseZ) * fieldForce;
          field[x][y].setLength(len);
          field[x][y].setAngle(angle);
        }
      }
    } else {
      for (let x = 0; x < columns; x++) {
        for (let y = 0; y < rows; y++) {
          const angle = noise.perlin3(x / 20, y / 20, noiseZ) * Math.PI * 2;
          const len = noise.perlin3(x / 40 + 40000, y / 40 + 40000, noiseZ) * fieldForce;
          field[x][y].setLength(len);
          field[x][y].setAngle(angle);
        }
      }
    }
  }

  function syncSize() {
    bindDom();
    if (!wrapEl || !canvas || !ctx) return false;
    const r = wrapEl.getBoundingClientRect();
    const cw = Math.max(1, Math.floor(r.width));
    const ch = Math.max(1, Math.floor(r.height));
    w = cw;
    h = ch;
    const dpr = Math.min(2, global.devicePixelRatio || 1);
    canvas.width = Math.floor(cw * dpr);
    canvas.height = Math.floor(ch * dpr);
    canvas.style.width = cw + 'px';
    canvas.style.height = ch + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    columns = Math.round(w / fieldSize) + 1;
    rows = Math.round(h / fieldSize) + 1;
    return true;
  }

  function resetSimulation() {
    if (!noise3D) noise.seed((Math.random() * 0xffffffff) >>> 0);
    if (!syncSize()) return;
    initParticles();
    initField();
  }

  function drawBackground() {
    if (!ctx) return;
    ctx.fillStyle = 'rgba(0,0,0,' + trailLength + ')';
    ctx.fillRect(0, 0, w, h);
  }

  function drawParticles() {
    if (!ctx) return;
    particles.forEach(function (p) {
      const ps = Math.abs(p.vel.x + p.vel.y) * particleSize + 0.3;
      ctx.fillStyle =
        'hsl(' +
        (hueBase + p.hue + (p.vel.x + p.vel.y) * hueRange) +
        ', 100%, 50%)';
      ctx.fillRect(p.pos.x, p.pos.y, ps, ps);
      const pos = p.pos.div(fieldSize);
      let v = null;
      if (pos.x >= 0 && pos.x < columns && pos.y >= 0 && pos.y < rows) {
        v = field[Math.floor(pos.x)][Math.floor(pos.y)];
      }
      p.move(v);
      p.wrap();
    });
  }

  function frame() {
    rafId = null;
    if (!running) return;
    if (!ldElRef || !ldElRef.classList.contains('ld--flowfield-active')) {
      stopInternal(false);
      return;
    }
    if (prefersReducedMotion()) {
      stopInternal(false);
      return;
    }
    calcField();
    noiseZ += noiseSpeed;
    drawBackground();
    drawParticles();
    rafId = global.requestAnimationFrame(frame);
  }

  function startLoop() {
    if (rafId != null) return;
    rafId = global.requestAnimationFrame(frame);
  }

  function stopInternal(clearDomClasses) {
    if (rafId != null) {
      global.cancelAnimationFrame(rafId);
      rafId = null;
    }
    running = false;
    unbindFlowfieldResize();
    if (clearDomClasses && ldElRef) {
      ldElRef.classList.remove('ld--flowfield-active', 'ld--flowfield-fullscreen');
    }
  }

  function startInline() {
    if (prefersReducedMotion()) return;
    bindDom();
    if (!ldElRef || !wrapEl || !canvas || !ctx) return;
    ldElRef.classList.add('ld--flowfield-active');
    ldElRef.classList.remove('ld--flowfield-fullscreen');
    noise.seed((Math.random() * 0xffffffff) >>> 0);
    noiseZ = 0;
    resetSimulation();
    running = true;
    bindFlowfieldResize();
    startLoop();
  }

  function goFullscreen() {
    bindDom();
    if (!ldElRef || !wrapEl || !canvas || !ctx) return;
    const cold = !ldElRef.classList.contains('ld--flowfield-active');
    /* Always arm Part 4 layout chrome on `#ld` (CSS `.ld--flowfield-fullscreen`).
       Never return early on `prefersReducedMotion()` — previously that skipped classes and
       left Part 4 at flat black without the centered flex shell; `@media (prefers-reduced-motion)`
       in ws-generator.css hides the canvas while keeping layout parity. */
    ldElRef.classList.add('ld--flowfield-active', 'ld--flowfield-fullscreen');
    if (cold) {
      noise.seed((Math.random() * 0xffffffff) >>> 0);
      noiseZ = 0;
    }
    const freezeMotion = prefersReducedMotion();
    /* One layout frame after class flip so `#ldFlowfield { inset:0 }` participates in sizing
       (`getBoundingClientRect()` was sometimes ~0-height in the same turn as `goPart4`). */
    global.requestAnimationFrame(function () {
      if (!ldElRef || !ldElRef.classList.contains('ld--flowfield-fullscreen')) return;
      if (!syncSize()) return;
      initParticles();
      initField();
      if (freezeMotion) {
        running = false;
        unbindFlowfieldResize();
        return;
      }
      running = true;
      bindFlowfieldResize();
      startLoop();
    });
  }

  function stop() {
    bindDom();
    stopInternal(true);
    noise3D = null;
  }

  global.wsLoaderFlowfield = {
    startInline: startInline,
    goFullscreen: goFullscreen,
    stop: stop,
  };

})(typeof window !== "undefined" ? window : this);
